import type { ReactNode } from 'react';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { CheckCircleIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react';

import { useAllSiteBlocks } from '@/hooks/use-all-site-blocks';
import { useSiteAdmins } from '@/hooks/use-site-admins';
import { usePublicSiteUrl } from '@/hooks/use-public-site-url';
import type { Page, Site } from '@/types/database';

interface HealthDrawerProps {
  open: boolean;
  onClose: () => void;
  pages: Page[];
  site: Site;
}

// The "Health" destination (spec section 5) — a small, honest set of
// checks backed by data this repo actually has. Deliberately does NOT
// include broken-link/accessibility crawling, stale-content-vs-departed-
// leader detection, domain/SSL expiry, or BeAScout-pin matching — none of
// that infrastructure exists yet, and a Health screen that fakes checks it
// can't really perform would be worse than a smaller, honest one.
export function HealthDrawer({ open, onClose, pages, site }: HealthDrawerProps) {
  const { admins } = useSiteAdmins();
  const { url: publicUrl } = usePublicSiteUrl();
  const { data: blocks } = useAllSiteBlocks(pages);

  const draftPages = pages.filter((page) => !page.is_published);
  const publicPages = pages.filter((page) => page.visibility === 'public');
  const emptyPages = pages.filter((page) => !(blocks ?? []).some((block) => block.page_id === page.id));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 } } } }}>
      <Stack direction="row" sx={{ alignItems: 'center', px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
          Health
        </Typography>
        <IconButton onClick={onClose} aria-label="Close">
          <XIcon size={20} />
        </IconButton>
      </Stack>

      <Stack spacing={2} sx={{ p: 2.5 }}>
        <HealthCheck
          ok={Boolean(publicUrl)}
          okText="Your site is published and reachable"
          warnText="Your site isn't published yet — publish a page to go live"
        />
        <HealthCheck
          ok={admins.length >= 2}
          okText={`${admins.length} admins can manage this site`}
          warnText="Only one admin can manage this site — see Permissions Review to add a second"
        />
        <HealthCheck
          ok={publicPages.length > 0}
          okText={`${publicPages.length} page${publicPages.length === 1 ? ' is' : 's are'} publicly visible`}
          warnText="Every page is set to Internal — your whole site is hidden from the public, which suppresses recruiting"
        />
        <HealthCheck
          ok={draftPages.length === 0}
          okText="No unpublished pages"
          warnText={`${draftPages.length} page${draftPages.length === 1 ? '' : 's'} still in draft: ${draftPages.map((page) => page.title).join(', ')}`}
        />
        <HealthCheck
          ok={emptyPages.length === 0}
          okText="No empty pages"
          warnText={`${emptyPages.length} page${emptyPages.length === 1 ? ' has' : 's have'} no content yet: ${emptyPages.map((page) => page.title).join(', ')}`}
        />
        <HealthCheck
          ok={site.calendar_visible_on_site}
          okText="Calendar is showing real upcoming events"
          warnText="Calendar isn't turned on yet — add a Calendar block and turn it on to show real events"
        />
      </Stack>
    </Drawer>
  );
}

function HealthCheck({ ok, okText, warnText }: { ok: boolean; okText: string; warnText: ReactNode }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      {ok ? (
        <CheckCircleIcon size={20} weight="fill" color="var(--mui-palette-success-main)" style={{ flexShrink: 0, marginTop: 2 }} />
      ) : (
        <WarningCircleIcon size={20} weight="fill" color="var(--mui-palette-warning-main)" style={{ flexShrink: 0, marginTop: 2 }} />
      )}
      <Box>
        <Typography variant="body2">{ok ? okText : warnText}</Typography>
      </Box>
    </Stack>
  );
}
